// TODO: Refactor out usage of the 'any' type throughout this file, then remove this suppression.
// Note especially the `MyType | any` unions (which just coalesce to `any`) - just the `| any` part
// should be removed once all callers are using the explicit types.
/* eslint-disable @typescript-eslint/no-explicit-any */

import { ApiResponse, BfiApi, IApiResponse } from './bfiApi';

export interface ILastLogin {
    firstName: string;
    lastName: string;
    businessName: string;
    lastLoginDate: Date;
}

export interface IUpdateBrokerPost {
    id?: number;
    firstName: string;
    lastName: string;
    arizonaLicenseNumber?: string;
    wireBankName?: string;
    wireRecipientName?: string;
    wireAccountLastFourDigits?: string;
    iccaTemplateId?: string;
    clientFeeType?: string;
    pointOfContactId?: string;
    hasSubmittedW9?: boolean;
    w9Url?: string;
    w9File?: File;
    businessName?: string;
    businessState?: string;
    title?: string;
    isFlagged?: boolean;
    backgroundCheckDate?: Date;
    acceptedTos?: boolean;
    fontId?: string;
    canSendTextMessages?: boolean;
    ipAddress?: string;
    businessAddress: IAddress;
    homeAddress: IAddress;
    phoneNumberA?: string;
    phoneNumberAType?: string;
    phoneNumberB?: string;
    phoneNumberBType?: string;
    email: string;
    secondaryEmail?: string;
    dateOfBirth?: Date;
}

export interface IBrokerListItem {
    id: number;
    name: string;
}

export interface IAddress {
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
}

export interface IStatusHistoryEntry {
    id: number;
    statusId: number;
    createdAt: string;
    createdBy: string;
    comments: string;
}

export interface IBrokerBClient {
    id: number;
    personName: string;
    clientName: string;
    status: string;
}

export interface IBrokerResponse {
    id: number;
    userId: string;
    title: string;
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    homePhone: string;
    workPhone: string;
    mobilePhone: string;
    isLocked: boolean;
    isDeleted: boolean;
    businessAddress: IAddress;
    email: string;
    secondaryEmail: string;
    brokerStatus: number;
    businessName: string;
    businessState: string;
    phoneNumberA: string;
    phoneNumberAType: string;
    phoneNumberB: string;
    phoneNumberBType: string;
    fontId: number | null;
    arizonaLicenseNumber: string;
    wireBankName: string;
    wireRecipientName: string;
    wireAccountLastFourDigits: string;
    canSendTextMessages: boolean;
    acceptedTos: boolean;
    isFlagged: boolean;
    hasSubmittedW9: boolean;
    w9FileName: string;
    pointOfContactId: string;
    homeAddress: IAddress;
    dateOfBirth: string | null;
    ipAddress: string;
}

export interface IBrokerAdminResponse extends IBrokerResponse {
    iccaTemplateId: number;
    clientFeeType: string;
    w9Url: string;
    backgroundCheckDate: string | null;
    lastEditJson: string | null;
    statusHistory: IStatusHistoryEntry[];
}

export interface IBrokerSettings {
    firstName: string | undefined;
    lastName: string | undefined;
    arizonaLicenseNumber: string | undefined;
    wireBankName: string | undefined;
    wireRecipientName: string | undefined;
    wireAccountLastFourDigits: string | undefined;
    businessName: string | undefined;
    businessState: string | undefined;
    title: string | undefined;
    businessAddress: IBrokerSettingsAddress;
    homeAddress: IBrokerSettingsAddress;
    phoneNumberA: string | undefined;
    phoneNumberAType: string | undefined;
    phoneNumberB: string | undefined;
    phoneNumberBType: string | undefined;
    emailAddressA: string | undefined;
    secondaryEmail: string | undefined;
    companyLogoUrl: string | undefined;
    dateOfBirth: Date | undefined;
}

interface IBrokerSettingsAddress {
    street1: string | undefined;
    street2: string | undefined;
    city: string | undefined;
    state: string | undefined;
    zipCode: string | undefined;
}

// Create/Update broker endpoints expect multipart/form-data (solely to support the W9 file
// upload!), so we need to convert the nested data structure into FormData with dot-notation keys
// (e.g. businessAddress.street1) and handle file and date types appropriately
const ensureFormData = (data: IUpdateBrokerPost | FormData): FormData => {
    if (data instanceof FormData) {
        // Legacy calls pass in as form data
        return data;
    }

    const formData = new FormData();
    const now = new Date();
    formData.append('createdAt', now.toString());
    formData.append('updatedAt', now.toString());

    const append = (key: string, value: unknown) => {
        if (value === null || value === undefined || value === '' || value === 'null') return;
        if (value instanceof File) {
            formData.append(key, value);
        } else if (value instanceof Date) {
            formData.append(key, value.toLocaleDateString('en-us'));
        } else if (typeof value === 'object') {
            for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
                append(`${key}.${k}`, v);
            }
        } else {
            formData.append(key, String(value));
        }
    };

    for (const [key, value] of Object.entries(data)) {
        append(key, value);
    }

    return formData;
};

export class BrokerApi extends BfiApi {
    getSingleBroker(brokerId: number): Promise<ApiResponse<IBrokerResponse | any>> {
        return super.get(`/brokers/${brokerId}/broker`);
    }

    updateBroker(broker: IUpdateBrokerPost | any): Promise<ApiResponse<IBrokerResponse> | any> {
        return super.post('/brokers/update', ensureFormData(broker), {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    getBrokerSettings = (brokerId: number): Promise<ApiResponse<IBrokerSettings>> => {
        return super.get(`/brokers/${brokerId}/settings`);
    };

    updateBrokerSettings = (broker: IBrokerSettings): Promise<any> => {
        return super.post('/brokers/settings', broker);
    };

    uploadCompanyLogo = (companyLogo: File): Promise<any> => {
        const formData = new FormData();
        formData.append('file', companyLogo);
        return super.post('/brokers/uploadCompanyLogo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    };

    sendBrokerActivationEmail = (brokerId: number) => {
        return super.get(`/brokers/${brokerId}/activationlink`);
    };

    getPendingItems = () => {
        return super.get('/brokers/pendingitems/');
    };

    search(searchText: string, pageNumber: number, pageSize: number): Promise<ApiResponse<IBrokerListItem>> {
        return super.get(
            `/brokers/search?searchText=${searchText ?? ''}&pageNumber=${pageNumber ?? 1}&pageSize=${pageSize ?? 50}`
        );
    }

    //Admin methods
    getAdminSingleBroker(brokerId: number): Promise<ApiResponse<IBrokerAdminResponse | any>> {
        return super.get(`/brokers/${brokerId}/admin`);
    }

    getLeanBroker(brokerId: number) {
        return super.get(`/brokers/${brokerId}/lean`);
    }

    activateBroker(id: number): Promise<IApiResponse> {
        return super.post(`/brokers/${id}/activate`);
    }

    reactivateBroker(id: number): Promise<IApiResponse> {
        return super.post(`/brokers/${id}/reactivate`);
    }

    rejectBroker(id: number, comment: string): Promise<IApiResponse> {
        return super.post('/brokers/reject', { id, comment });
    }

    createBroker(broker: IUpdateBrokerPost | any): Promise<ApiResponse<IBrokerResponse | any>> {
        return super.post('/brokers/create', ensureFormData(broker), {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    validateExistingBroker(broker: IUpdateBrokerPost | any): Promise<ApiResponse<boolean | any>> {
        return super.post('/brokers/validateexistingbroker', ensureFormData(broker), {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    updateAdminBroker(broker: IUpdateBrokerPost | any): Promise<ApiResponse<IBrokerAdminResponse> | any> {
        return super.post('/brokers/update/admin', ensureFormData(broker), {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    getLastBrokerLogins(hours: number): Promise<ApiResponse<ILastLogin>> {
        return super.get(`/brokers/logins/${hours.toString()}`);
    }

    getActiveBrokerClientSummary(): Promise<IApiResponse> {
        return super.get('/brokers/activebclientsummary/');
    }

    getPendingBrokerGridRows(): Promise<IApiResponse> {
        return super.post('/brokers/getBrokerGridRowsByFilter/', {
            pending: 1,
        });
    }

    getSentBrokerGridRows(): Promise<IApiResponse> {
        return super.post('/brokers/getBrokerGridRowsByFilter/', { sent: 1 });
    }

    getDraftBrokerGridRows(): Promise<IApiResponse> {
        return super.post('/brokers/getBrokerGridRowsByFilter/', { draft: 1 });
    }

    getActiveBrokerGridRows(): Promise<IApiResponse> {
        return super.post('/brokers/getBrokerGridRowsByFilter/', { active: 1 });
    }

    getRejectedBrokerGridRows(): Promise<IApiResponse> {
        return super.post('/brokers/getBrokerGridRowsByFilter/', {
            rejected: 1,
        });
    }

    deleteBroker(id: number): Promise<IApiResponse> {
        return super.delete(`/brokers/${id}/delete`);
    }

    getBrokerBClients(brokerId: number): Promise<ApiResponse<IBrokerBClient[] | any>> {
        return super.get(`brokers/${brokerId}/bclients`);
    }

    getIncompleteRegisteredBroker(userId: number): Promise<IApiResponse> {
        return super.get(`/brokers/incomplete/${userId}/user`);
    }

    deleteIncompleteRegisteredBroker(userId: number): Promise<IApiResponse> {
        return super.delete(`/brokers/incomplete/${userId}/delete`);
    }
}
